import { Injectable, isDevMode } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from "@angular/router";

/**
 * Strategie de titre dynamique pour les onglets navigateur.
 *
 * - Si la route definit un `title`, il est utilise tel quel.
 * - Sinon, fallback sur "EVA".
 *
 * Les pages auth incluent deja le prefixe "EVA-IT - " dans leur title.
 * Les modules app utilisent un titre simple (ex: "Clients", "Tableau de bord").
 *
 * Routes avec titre dynamique (`data: { dynamicTitle: true }`) :
 * la strategy n'ecrase pas le titre sur navigation interne (meme route config,
 * ex. changement de queryParam/tab). Le composant reste maitre du titre et
 * peut appeler `Title.setTitle(entity.name)` sans etre reecrit.
 */
@Injectable()
export class EvaTitleStrategy extends TitleStrategy {
  private previousRouteConfigPath: string | null = null;

  constructor(private readonly title: Title) {
    super();
  }

  override updateTitle(routerState: RouterStateSnapshot): void {
    const deepest = this.getDeepestActivatedRoute(routerState.root);
    const currentPath = deepest?.routeConfig?.path ?? null;
    const isDynamic = deepest?.data?.["dynamicTitle"] === true;

    // Navigation interne (meme route config) sur une page a titre dynamique :
    // on respecte le titre pose par le composant (ex. nom du chantier)
    if (isDynamic && currentPath !== null && currentPath === this.previousRouteConfigPath) {
      return;
    }

    this.previousRouteConfigPath = currentPath;
    const title = this.buildTitle(routerState);

    if (!title && isDevMode()) {
      const url = routerState.url;
      const pathChain = this.collectPathChain(routerState.root);
      console.error(
        `[EvaTitleStrategy] Route sans "title" — fallback "EVA" appliqué.\n  URL: ${url}\n  Route chain: ${pathChain}\n  Fix: ajouter \`title: "<Nom module>"\` sur la route dans le routing module. Pour les routes détail (:id), ajouter aussi \`data: { dynamicTitle: true }\` et appeler \`Title.setTitle(entity.name)\` dans le composant.`,
      );
    }

    this.title.setTitle(title || "EVA");
  }

  private collectPathChain(route: ActivatedRouteSnapshot): string {
    const parts: string[] = [];
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      const p = current.routeConfig?.path;
      if (p !== undefined && p !== "") parts.push(p);
      current = current.firstChild;
    }
    return parts.join(" / ") || "(root)";
  }

  private getDeepestActivatedRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let current = route;
    while (current.firstChild) current = current.firstChild;
    return current;
  }
}
